import React, { useEffect, useState } from "react";
import { t } from "i18next";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import { notifyError, notifySuccess } from "../../utils/toast";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const UploaderCrop = ({
  setImageUrl,
  imageUrl,
  is_multiple,
  no_of_image,
  folder,
  name_merge,
  onImageUpload,
}) => {
  const adminInfo = JSON.parse(localStorage.getItem("adminInfo"));
  const authToken = adminInfo?.token;

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setError] = useState("");
  const [crop, setCrop] = useState({ aspect: 3, width: 150, height: 50 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [imageRef, setImageRef] = useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/jpg, image/png, image/svg+xml",
    multiple: is_multiple,
    maxSize: 155000,
    maxFiles: no_of_image,
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      );
    },
  });

  const onImageLoad = (image) => {
    setImageRef(image);
  };

  const cropImage = async () => {
    if (imageRef && completedCrop) {
      const canvas = document.createElement("canvas");
      const scaleX = imageRef.naturalWidth / imageRef.width;
      const scaleY = imageRef.naturalHeight / imageRef.height;
      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        imageRef,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );

      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", 1);
      });

      setCroppedImage(URL.createObjectURL(blob));
    }
  };

  useEffect(() => {
    if (completedCrop) {
      cropImage();
    }
            // eslint-disable-next-line 
  }, [completedCrop]);

  const uploadCroppedImage = async () => {
    if (files && files.length > 0) {
      const imageUrlCount = Array.isArray(imageUrl)
        ? imageUrl.length
        : imageUrl
        ? 1
        : 0;
      if (is_multiple && imageUrlCount + files?.length > no_of_image) {
        return notifyError(
          `Maximum ${no_of_image ? no_of_image : 2} Image Can be Upload!`
        );
      }

      setLoading(true);
      setError("Uploading....");
      const formData = new FormData();

      // Use croppedImage instead of original file
      if (croppedImage) {
        const blob = await fetch(croppedImage).then((res) => res.blob());
        formData.append("file", blob, "croppedImage.jpg");
      }

      formData.append("folderName", folder);
      formData.append("name_merge", name_merge);

      axios({
        url:
          process.env.REACT_APP_API_BASE_URL +
          "/upload/" +
          (files.length === 1 ? "upload-single" : "upload-multiple") +
          "/" +
          name_merge +
          "/" +
          folder,
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
           "Authorization": `Bearer ${authToken}`
        },
        data: formData,
      })
        .then((res) => {
          notifySuccess("Image Uploaded successfully!");
          setLoading(false);
          if (res && res.data.message !== "") {
            setImageUrl(res.data.image_path);
            if (onImageUpload) onImageUpload(res.data.image_path);
          }
        })
        .catch((err) => {
          console.error("err", err);
          notifyError(err.Message);
          setLoading(false);
        });
    }
  };

  return (
    <div className="w-full text-center">
      {!(
        (is_multiple &&
          (files.length > 0 ||
            (Array.isArray(imageUrl) && imageUrl.length > 0))) ||
        (!is_multiple && (imageUrl || files.length > 0))
      ) && (
        <div
          className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md cursor-pointer px-6 pt-5 pb-6"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <span className="mx-auto flex justify-center">
            <FiUploadCloud className="text-3xl text-green-500" />
          </span>
          <p className="text-sm mt-2">{t("DragYourImage")}</p>
          <em className="text-xs text-gray-400">{t("imageFormat")}</em>
        </div>
      )}
      <div className="mt-4 text-xs text-gray-500">
        <p>Max file size: 150 KB, height: 50px, width: 150px.</p>
      </div>
      <div className="text-green-500">{loading && err}</div>

      {files.length > 0 && (
        <ReactCrop
          crop={crop}
          onChange={(newCrop) => setCrop(newCrop)}
          onComplete={(newCrop) => setCompletedCrop(newCrop)}
          aspect={3}
        >
          <img
            src={files[0].preview}
            alt="Preview"
            onLoad={(e) => onImageLoad(e.currentTarget)}
          />
        </ReactCrop>
      )}

      {croppedImage && (
        <div className="mt-4 flex items-center justify-around">
          <img
            src={croppedImage}
            alt="Cropped"
            className="border rounded-md"
            style={{ height: "50px", width: "150px" }}
          />
          <button
            type="button"
            onClick={uploadCroppedImage}
            disabled={!completedCrop || loading}
            className={`px-4 py-2 text-white rounded ${
              loading ? "bg-gray-400" : "bg-green-500"
            }`}
          >
            {loading ? "Uploading..." : "Upload Cropped Image"}
          </button>
        </div>
      )}
    </div>
  );
};

export default UploaderCrop;
